import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { DropdownMenu } from '#pplx/frontend/aether/components/DropdownMenu/index.js';
import { Input } from '#pplx/frontend/aether/components/Input/index.js';
import { useMenu } from '#pplx/frontend/aether/components/useMenu/index.js';
import { useScrollArea } from '#pplx/frontend/aether/components/useScrollArea/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useSourcesMenuConfig } from '#pplx/frontend/apps/ask/ask-input/ask-input-sources-menu/components/SourcesMenuConfigContextSupport.js';
import { SourcesMenuItems } from '#pplx/frontend/apps/ask/ask-input/ask-input-sources-menu/components/SourcesMenuItems.js';
import { SourcesMenuUpsellMenuItem } from '#pplx/frontend/apps/ask/ask-input/ask-input-sources-menu/components/SourcesMenuUpsellMenuItem.js';
import { isPassivelyConnectedConnector } from '#pplx/frontend/apps/ask/sources/hooks/sourceStatusPredicates.js';
import { useSources } from '#pplx/frontend/apps/ask/sources/hooks/useSources.js';
import { isSourceOmitted } from '#pplx/frontend/apps/ask/sources/types/omittedSources.js';
import { useSourceActivity } from '#pplx/frontend/apps/ask/user-settings/hooks/useSourceActivity.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useDebouncedValue } from '#pplx/frontend/common/hooks/useDebouncedValue.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import classNames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
const sourceToggledEvent = EVENT_PAYLOAD;
/**
 * If a source with the key is enabled, the dependent sources in the array will also be enabled.
 */
const SOURCE_REQUIREMENTS = {
    crunchbase: ['web'],
};
/**
 * If a source with the key is disabled, the sources in the array will also be disabled.
 */
const SOURCE_DEPENDENCIES = {
    web: ['crunchbase'],
};
export const SourcesMenuContent = memo(function SourcesMenuContent({ showSearch = true, sourcesOrder = 'canonical', sources, onChange, onSourceMentionSelect, onSourceMentionDeselect, suggestedSources = EMPTY_ARRAY, omittedSources = EMPTY_ARRAY, omitCometMcpSources = false, isComputer = false, searchQuery: controlledSearchQuery, setSearchQuery: setControlledSearchQuery, }) {
    const { $t } = useIntl();
    const { hasAccessToProFeatures } = useIsSubscribed();
    const [internalSearchQuery, setInternalSearchQuery] = useState('');
    const searchQuery = controlledSearchQuery ?? internalSearchQuery;
    const setSearchQuery = setControlledSearchQuery ?? setInternalSearchQuery;
    const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
    const { sources: allSources, isSourceConnected, getSource, getSourceLabel, } = useSources({
        omitCometMcpSources,
        omittedSources,
        searchQuery: debouncedSearchQuery,
        productSurface: isComputer ? 'computer' : 'ask',
    });
    const isOmitted = useCallback((sourceId) => isSourceOmitted(getSource(sourceId), omittedSources), [omittedSources, getSource]);
    const { trackSourceActivity } = useSourceActivity();
    const { isMobileStyle } = useAetherContext();
    const { hideDescriptionOverlays, hideScrollFade } = useSourcesMenuConfig();
    const { canScrollUp, canScrollDown } = useScrollArea();
    const { track } = useInstrumentation();
    const menu = useMenu();
    const menuSources = useMemo(() => {
        const menuSources = new Set();
        const isSearchQuery = debouncedSearchQuery.trim() !== '';
        if (!isSearchQuery) {
            if (sourcesOrder === 'canonical') {
                suggestedSources.forEach((source) => {
                    if (isSourceConnected(source)) {
                        menuSources.add(source);
                    }
                });
            }
            sources.forEach((source) => {
                const existsInSources = allSources.includes(source);
                if (!existsInSources && isSourceConnected(source)) {
                    menuSources.add(source);
                }
            });
        }
        allSources.filter(isSourceConnected).forEach((source) => {
            // Skip connectors that report as connected only because they are no-auth
            // or system-auth (e.g. premium sources the user has not personally
            // OAuthed). They stay reachable via search, and once the user toggles
            // one on it persists via the suggestedSources / sources paths above.
            if (sourcesOrder === 'canonical' && !isSearchQuery && !menuSources.has(source)) {
                const apiSource = getSource(source);
                if (apiSource && isPassivelyConnectedConnector(apiSource)) {
                    return;
                }
            }
            menuSources.add(source);
        });
        return Array.from(menuSources).filter((source) => !isOmitted(source));
    }, [
        allSources,
        isSourceConnected,
        suggestedSources,
        isOmitted,
        sources,
        debouncedSearchQuery,
        getSource,
        sourcesOrder,
    ]);
    const isSourceEnabled = useCallback((source) => sources.includes(source), [sources]);
    const enableSource = useCallback((source) => {
        trackSourceActivity(source);
        const newSources = new Set([...sources, source]);
        // Enable dependencies
        SOURCE_REQUIREMENTS[source]?.forEach((requiredSource) => {
            newSources.add(requiredSource);
        });
        if (source !== 'web') {
            onSourceMentionSelect?.(source, getSourceLabel(source));
        }
        onChange(Array.from(newSources));
    }, [sources, onChange, onSourceMentionSelect, getSourceLabel, trackSourceActivity]);
    const disableSource = useCallback((source) => {
        const newSources = new Set(sources);
        const removedSources = new Set([source]);
        newSources.delete(source);
        // Disable required sources
        SOURCE_DEPENDENCIES[source]?.forEach((dependentSource) => {
            newSources.delete(dependentSource);
            removedSources.add(dependentSource);
        });
        removedSources.forEach((removedSource) => {
            if (removedSource !== 'web') {
                onSourceMentionDeselect?.(removedSource);
            }
        });
        onChange(Array.from(newSources));
    }, [sources, onChange, onSourceMentionDeselect]);
    const onSourceToggled = useCallback((source, enabled) => {
        const isSource = isSourceEnabled(source);
        if (isSource === enabled) {
            return;
        }
        track(sourceToggledEvent({ name: 'source toggled', data: { source, enabled } }));
        enabled ? enableSource(source) : disableSource(source);
    }, [isSourceEnabled, enableSource, disableSource, track]);
    // Prevents radix from focusing menu items when typing in the search box
    const onKeyDown = useCallback((event) => {
        // Prevent arrow from moving to the end of the input value and rather focus the first menu item
        if (event.key === 'ArrowDown') {
            // WHY preventDefault: suppress default cursor-to-end behavior so focus moves to the first menu item
            event.preventDefault();
            menu?.focusFirstMenuItem();
            return;
        }
        // WHY stopPropagation: prevent Radix from interpreting keystrokes as menu item navigation while typing in the search input
        event.stopPropagation();
        if (event.key === 'Enter') {
            // WHY preventDefault: suppress Radix's default Enter handling (which would close the menu) and instead apply the sources-menu Enter behavior
            event.preventDefault();
            const firstSource = menuSources[0];
            if (firstSource) {
                const isEnabled = isSourceEnabled(firstSource);
                onSourceToggled(firstSource, !isEnabled);
            }
        }
    }, [menu, menuSources, isSourceEnabled, onSourceToggled]);
    return (_jsxs("div", { className: "isolate", children: [!hasAccessToProFeatures && (_jsxs(_Fragment, { children: [_jsx(SourcesMenuUpsellMenuItem, {}), _jsx(DropdownMenu.Separator, {})] })), hasAccessToProFeatures && showSearch && (_jsx(BoxPrimitive, { bg: isMobileStyle ? 'background' : 'raised', className: classNames('py-1 md:sticky top-0 z-10', hideDescriptionOverlays ? 'px-2' : 'md:px-1', 'transition-shadow duration-normal', canScrollUp
                    ? 'md:shadow-[0_6px_6px_-2px_rgb(from_var(--bg-raised)_r_g_b_/_0.9)]'
                    : 'md:shadow-none'), onKeyDown: onKeyDown, children: _jsx(Input, { type: "search", size: "small", "aria-label": $t({
                        defaultMessage: 'Search connectors',
                        description: 'Placeholder text for the search input in the connectors menu',
                        id: '36930e879a',
                    }), placeholder: $t({
                        defaultMessage: 'Search connectors',
                        description: 'Placeholder text for the search input in the connectors menu',
                        id: '36930e879a',
                    }), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), onClear: () => setSearchQuery('') }) })), _jsx(SourcesMenuItems, { sources: menuSources, isSourceEnabled: isSourceEnabled, onSourceToggled: onSourceToggled, getIsSourceConnected: isSourceConnected, getSource: getSource }), menuSources.length === 0 && debouncedSearchQuery.trim() !== '' && (_jsx("div", { className: "py-4 px-2", children: _jsx(TextPrimitive, { variant: "small", color: "light", textAlign: "center", display: "block", children: _jsx(FormattedMessage, { defaultMessage: "No results", description: "Empty state shown in the sources menu when the search query has no matches", id: "4a0237a918" }) }) })), !hideScrollFade && (_jsx("div", { "aria-hidden": true, className: classNames('hidden md:block', 'sticky bottom-0 h-4 -mt-4 pointer-events-none', 'bg-linear-to-t from-raised to-transparent', 'transition-opacity duration-normal', canScrollDown ? 'opacity-100' : 'opacity-0') }))] }));
});
//# sourceMappingURL=SourcesMenuContent.js.map